import React, { useEffect, useState } from 'react';
import { Tabs, Modal, Spin } from 'antd';
import './index.less';
import SoilWeatherMapComponent from '@/page/WeatherStation/weather-query/Component/SoilWeatherMapComponent';
import { IHierarchy, IWeatherChardDataReqModel, IWeatherStationModel, IWeatherStationReqModel, IWeatherStationResModel } from '@/util/Request/requestType';
import SoilChartComponent from '@/page/WeatherStation/weather-query/Component/SoilChartComponent';
import { useDispatch } from 'react-redux';
import { updateChartData, updateUnderChartState } from '@/redux/reducer/weatherStation';
import { weatherDownloadXls, weatherGetChartData, getNewHierarchies } from '@/util/Request';
import Download from '@/page/Soil/soil-profile/Component/Download';
import WeatherApiQueryComponent from '@/page/WeatherStation/weather-query/Component/WeatherApiQueryComponent';
import type { IQueryDataShow } from '@/page/WeatherStation/weather-query/Component/WeatherApiQueryComponent';
import moment from 'moment';
import { SoilCloseIcon } from '@/resource/IconComponentSets';

const { confirm } = Modal;


interface QueryDetailsProps {
    queryId?: number
    data: IWeatherStationResModel
    // double call back
    polyPathCallback?: (pathPoint: string) => void
    requestParam?: IWeatherStationReqModel
}

interface ICoordinate {
    lat: number
    lng: number
}

enum TabsEnum {
    Map = 'Map',
    Chart = 'Chart',
    APIQuery = 'API Query'
}

const WeatherStationQueryDetails = (props: QueryDetailsProps) => {
    const {
        polyPathCallback,
        data,
        requestParam
    } = props;

    const dispatch = useDispatch();
    const [coordinateReq, setCoordinateReq] = useState<ICoordinate[]>([]);
    const [currenTab, setCurrenTab] = useState<TabsEnum>(TabsEnum.Map);
    const [stationData, setStationData] = useState<IWeatherStationModel[]>([]);
    const [weatherProperties, setWeatherProperties] = useState<IHierarchy[] | undefined>(undefined);
    const [downloadVisible, setDownloadVisible] = useState(false);
    const [downloadSpining, setDownloadSpining] = useState(false);
    const [downloadEnable, setDownloadEnable] = useState(false);

    // SECTION: useEffect area =====================================================================================
    useEffect(() => {
        if (coordinateReq.length > 0) {
            const constructReqParam = `POLYGON((${coordinateReq?.map(coordinate => `${coordinate.lng} ${coordinate.lat}`)}))`;
            polyPathCallback(constructReqParam);
        }
    }, [coordinateReq]);


    const showWarningMessage = (message: string) => {
        confirm({
            className: 'soil-profile-warning-div',
            title: 'Error',
            content: message,
            onOk() {
                console.log('OK');
            },
            onCancel() {
                console.log('Cancel');
            },
        });
    };

    const downloadClicked = async (type: 'JSON' | 'XLS' | 'XLSX' | 'PDF' | 'CSV' | 'txt') => {
        const {
            startDate,
            endDate
        } = requestParam;
        const downloadContent: IQueryDataShow[] = data?.queryStations?.map((data) => {
            const stations = data.stations.map((station) => station.stationID);
            const newStartDate = startDate.split('/').reverse().join('');
            const newEndDate = endDate.split('/').reverse().join('');
            return {
                stations,
                frequency: data.frequency,
                supplier: data.supplier,
                variables: [data.variable],
                format: 'JSON',
                datefrom: newStartDate,
                dateto: newEndDate
            };
        });
        if (!downloadContent) {
            return showWarningMessage('Current query result was null, cannot be download !');
        }
        setDownloadSpining(true);
        const dateString = moment().format('YYYY/MM/DD'); //当前时间
        try {
            const dateTimeStamp = new Date().getTime();
            const fileName = `weather_data_${dateString}`;
            if (type === 'XLSX') {
                await weatherDownloadXls(requestParam, type, `${fileName}_${dateTimeStamp}.xlsx`);
            }
            if (type === 'txt' && downloadContent) {
                downloadContent.forEach((download) => {
                    const apiQueryName = `${download.supplier}-${download.variables}-${download.frequency}_${dateString}`;
                    const a = document.getElementById('download-query') as HTMLAnchorElement;
                    a.download = `${apiQueryName}.txt`;
                    const blob = new Blob([JSON.stringify(download)]);
                    a.href = URL.createObjectURL(blob);
                    a.click();
                });
            }
            setDownloadSpining(false);
        } catch (err) {
            setDownloadSpining(false);
        }
    };

    useEffect(() => {
        switch (currenTab) {
            case TabsEnum.Map: {
                dispatch(updateUnderChartState(false));
                break;
            }
            case TabsEnum.Chart: {
                dispatch(updateUnderChartState(true));
                break;
            }
            case TabsEnum.APIQuery: {
                dispatch(updateUnderChartState(false));
                break;
            }
        }
    }, [currenTab]);

    useEffect(() => {
        if (data) {
            requestForChart();
            setDownloadEnable(true);
        } else {
            setDownloadEnable(false);
        }
    }, [data]);

    useEffect(() => {
        if (requestParam && requestParam.hierarchiesDetails) {
            getNewHierarchies(requestParam.hierarchiesDetails).then(data => {
                setWeatherProperties(data);
            });
        }
    }, [requestParam]);

    // SECTION: func   area =====================================================================================
    const requestForChart = async () => {
        try {

            let mapStations: IWeatherStationModel[] = [];
            for (const mapStationItem of data.mapStations) {
                mapStations = mapStations.concat(mapStationItem.stations);
            }

            const reqParam: IWeatherChardDataReqModel = {
                options: requestParam,
                stations: mapStations
            };
            setStationData(mapStations);
            const res = await weatherGetChartData(reqParam);
            // update the chart data to the redux
            dispatch(updateChartData(res));
        } catch (err) {
            console.log('res:=', err);
        }
    };

    // SECTION: render area =====================================================================================
    const items = [
        {
            label: TabsEnum.Map.toString(), key: TabsEnum.Map, children: (
                <SoilWeatherMapComponent
                    mapData={data?.mapStations}
                    polyPathCallback={(coordinates: ICoordinate[]) => setCoordinateReq(coordinates)}
                />
            )
        },
        {
            label: TabsEnum.Chart.toString(), key: TabsEnum.Chart, children: (
                <SoilChartComponent stationData={stationData}
                    weatherProperties={weatherProperties}
                />
            )
        },
        {
            label: TabsEnum.APIQuery.toString(), key: TabsEnum.APIQuery, children: (
                <WeatherApiQueryComponent
                    queryData={data?.queryStations}
                    startDate={requestParam?.startDate}
                    endDate={requestParam?.endDate}
                />
            )
        },
    ];

    return (
        <div className='weather-station-super-div'>
            <Modal
                open={downloadVisible}
                onOk={() => setDownloadVisible(false)}
                onCancel={() => setDownloadVisible(false)}
                style={{
                    height: 'auto',
                    position: 'absolute',
                    right: '100px',
                    top: '300px',
                }}
                className='down-load-modal'
                title='Choose file format'
                closeIcon={<SoilCloseIcon />}
            >
                <Spin spinning={downloadSpining}>
                    <Download
                        className='weather-down-load-div-container'
                        titles={['Data Excel', 'API Query']}
                        selectSpecifyType={(type) => downloadClicked(type)}
                        cacncelDownload={() => {
                            setDownloadVisible(false);
                        }}
                    />
                </Spin>
            </Modal>
            <div className='weather-station-right-menu'>
                <button
                    className="btn-download"
                    style={{ backgroundColor: downloadEnable ? '#59B948' : 'lightGray' }}
                    onClick={() => {
                        if (downloadEnable) {
                            setDownloadVisible(true);
                        }
                    }}
                >
                    Download
                </button>
            </div>
            <div className='weather-station-chart-div'>
                <Tabs
                    size='small'
                    className='tabs-super-div'
                    onChange={(currentKey: TabsEnum) => {
                        setCurrenTab(currentKey);
                    }}
                    items={items}
                    style={{ height: '100%' }}
                />
            </div>
            <a id='download-query' />
        </div>
    );
};

export default WeatherStationQueryDetails;